import { debounce } from 'lodash';

export default class Events {
    constructor() {
        this.$ = $ || window.jQuery;

        this.framing = {
            pause: false,
            fps: Math.ceil(1000 / 60), // About 60fps
        };

        this.vw = this.$(window).width();

        this.callbacks = {
            scroll: {
                methods: [],
                initialized: false,
            },
            scrollStart: {
                methods: [],
                initialized: false,
            },
            scrollStop: {
                methods: [],
                initialized: false,
            },
            scrollFramed: {
                methods: [],
                initialized: false,
            },
            scrollDownStart: {
                methods: [],
                initialized: false,
            },
            scrollUpStart: {
                methods: [],
                initialized: false,
            },
            resize: {
                methods: [],
                initialized: false,
            },
            resizeHorizontal: {
                methods: [],
                initialized: false,
            },
        };
    }

    /**
     * Executes a callback during window scroll
     * @param {function} callback The method you want to invoke
     */
    onScroll(callback) {
        if (typeof callback !== 'function') {
            return;
        }

        this.callbacks.scroll.methods.push(callback);
        if (this.callbacks.scroll.initialized) {
            return;
        }
        this.callbacks.scroll.initialized = true;

        this.$(window).on('scroll', (e) => {
            this.callbacks.scroll.methods.forEach((cb) => {
                cb(e);
            });
        });
    }

    /**
     * Executes a callback when window scroll starts
     * @param {function} callback The method you want to invoke
     */
    onScrollStart(callback) {
        let timeout = 0;
        let isScrolling = false;

        if (typeof callback !== 'function') {
            return;
        }

        this.callbacks.scrollStart.methods.push(callback);
        if (this.callbacks.scrollStart.initialized) {
            return;
        }
        this.callbacks.scrollStart.initialized = true;

        this.$(window).on('scroll', (e) => {
            if (!isScrolling) {
                this.callbacks.scrollStart.methods.forEach((cb) => {
                    cb(e);
                });
            }
            isScrolling = true;
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                isScrolling = false;
            }, 100);
        });
    }

    /**
     * Executes a callback when window scroll stops
     * @param {function} callback The method you want to invoke
     */
    onScrollStop(callback) {
        if (typeof callback !== 'function') {
            return;
        }

        this.callbacks.scrollStop.methods.push(callback);

        if (this.callbacks.scrollStop.initialized) {
            return;
        }
        this.callbacks.scrollStop.initialized = true;

        this.$(window).on('scroll', debounce((e) => {
            this.callbacks.scrollStop.methods.forEach((cb) => {
                cb(e);
            });
        }, 100));
    }

    /**
     * Executes a callback every 60fps when window scrolls. Useful to reduce computing load.
     * @param {function} callback The method you want to invoke
     */
    onScrollFramed(callback) {
        if (typeof callback !== 'function') {
            return;
        }

        this.callbacks.scrollFramed.methods.push(callback);
        if (this.callbacks.scrollFramed.initialized) {
            return;
        }
        this.callbacks.scrollFramed.initialized = true;

        this.$(window).on('scroll', (e) => {
            if (this.framing.pause) {
                return;
            }

            this.framing.pause = true;

            setTimeout(() => {
                this.callbacks.scrollFramed.methods.forEach((cb) => {
                    cb(e);
                });

                this.framing.pause = false;
            }, this.framing.fps);
        });
    }

    /**
     * Executes a callback when window start scrolling downward
     * @param {function} callback The method you want to invoke
     */
    onScrollDownStart(callback) {
        if (typeof callback !== 'function') {
            return;
        }

        this.callbacks.scrollDownStart.methods.push(callback);
        if (this.callbacks.scrollDownStart.initialized) {
            return;
        }
        this.callbacks.scrollDownStart.initialized = true;

        let storedOffset = this.$(window).scrollTop();
        let timeout = 0;
        let lock = false;

        this.$(window).on('scroll', (e) => {
            const newOffset = this.$(window).scrollTop();

            if (lock && newOffset < storedOffset) {
                lock = false;
            }
            if (!lock && newOffset > storedOffset) {
                this.callbacks.scrollDownStart.methods.forEach((cb) => {
                    cb(e);
                });
                lock = true;
            }

            storedOffset = newOffset;

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                lock = false;
            }, 100);
        });
    }

    /**
     * Executes a callback when window start scrolling upward
     * @param {function} callback The method you want to invoke
     */
    onScrollUpStart(callback) {
        if (typeof callback !== 'function') {
            return;
        }

        this.callbacks.scrollUpStart.methods.push(callback);
        if (this.callbacks.scrollUpStart.initialized) {
            return;
        }
        this.callbacks.scrollUpStart.initialized = true;

        let storedOffset = this.$(window).scrollTop();
        let timeout = 0;
        let lock = false;

        this.$(window).on('scroll', (e) => {
            const newOffset = this.$(window).scrollTop();

            if (lock && newOffset > storedOffset) {
                lock = false;
            }
            if (!lock && newOffset < storedOffset) {
                this.callbacks.scrollUpStart.methods.forEach((cb) => {
                    cb(e);
                });
                lock = true;
            }

            storedOffset = newOffset;

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                lock = false;
            }, 100);
        });
    }

    /**
     * Executes a callback during window resize
     * @param {function} callback The method you want to invoke
     */
    onResize(callback) {
        if (typeof callback !== 'function') {
            return;
        }

        this.callbacks.resize.methods.push(callback);
        if (this.callbacks.resize.initialized) {
            return;
        }
        this.callbacks.resize.initialized = true;

        this.$(window).on('resize', (e) => {
            this.callbacks.resize.methods.forEach((cb) => {
                cb(e);
            });
        });
    }

    /**
     * Executes a callback during window resize but only if resize is Horizontal.
     * Useful for Android browsers where the nav bar triggers window resize during scroll.
     * @param {function} callback The method you want to invoke
     */
    onResizeHorizontal(callback) {
        if (typeof callback !== 'function') {
            return;
        }

        this.callbacks.resizeHorizontal.methods.push(callback);
        if (this.callbacks.resizeHorizontal.initialized) {
            return;
        }
        this.callbacks.resizeHorizontal.initialized = true;

        this.$(window).on('resize', debounce((e) => {
            if (this.vw !== this.$(window).width()) {
                this.vw = this.$(window).width();
                this.callbacks.resizeHorizontal.methods.forEach((cb) => {
                    cb(e);
                });
            }
        }, this.framing.fps));
    }

    /**
     * Returns registered callbacks to let developer delete or alter them.
     * If you use named functions, you can retrieve them like this: window.events.callbacks.resize[idx].name;
     * @returns {array} Registered callback
     */
    getCallbacks() {
        return this.callbacks;
    }
}
